/* assets/floating-video.js
 - Muted-by-default, overlay/close handling
 - Configurable close persistence: 'none' | 'session' | 'expire' | 'local'
   - 'none'   : no persistence (close only for current view; reload shows again)
   - 'session': sessionStorage (survives reloads in same tab)
   - 'expire' : localStorage with expiry (ms)
   - 'local'  : localStorage permanent
*/

(function () {
  // CONFIG: choose persistence mode here
  const persistenceMode = 'none'; // 'none' | 'session' | 'expire' | 'local'
  const expireMs = 24 * 60 * 60 * 1000; // 24 hours for 'expire' mode

  function storageGet(key) {
    try {
      if (persistenceMode === 'session') return sessionStorage.getItem(key);
      if (persistenceMode === 'none') return null;
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function storageSet(key, value) {
    try {
      if (persistenceMode === 'session') return sessionStorage.setItem(key, value);
      if (persistenceMode === 'none') return; // don't persist
      return localStorage.setItem(key, value);
    } catch (e) {
      return;
    }
  }
  function storageRemove(key) {
    try {
      if (persistenceMode === 'session') return sessionStorage.removeItem(key);
      if (persistenceMode === 'none') return;
      return localStorage.removeItem(key);
    } catch (e) {
      return;
    }
  }

  function isClosedKeyExpired(raw) {
    if (!raw) return true;
    try {
      const obj = JSON.parse(raw);
      if (!obj || !obj.t) return true;
      const ts = obj.t;
      return (Date.now() - ts) > expireMs;
    } catch (e) {
      return true;
    }
  }

  function markClosed(storageKey) {
    if (persistenceMode === 'none') return; // do not persist
    if (persistenceMode === 'expire') {
      storageSet(storageKey, JSON.stringify({ t: Date.now() }));
    } else {
      storageSet(storageKey, '1');
    }
  }

  function isClosed(storageKey) {
    if (persistenceMode === 'none') return false;
    const raw = storageGet(storageKey);
    if (!raw) return false;
    if (persistenceMode === 'expire') {
      return !isClosedKeyExpired(raw);
    }
    return raw === '1' || raw === '"1"';
  }

  function initFloating(wrapper) {
    if (!wrapper) return;
    const productId = wrapper.getAttribute('data-product-id') || 'global';
    const storageKey = 'tt_fv_closed_' + productId;

    // If closed/persisted, do not show (unless persistenceMode is 'none')
    if (isClosed(storageKey)) return;

    const video = wrapper.querySelector('.tt-fv-player');
    const closeBtn = wrapper.querySelector('.tt-fv-close');
    const overlay = wrapper.querySelector('.tt-fv-play-overlay');
    let shown = false;

    // Ensure video is muted by default and controls removed
    if (video) {
      try {
        video.removeAttribute('controls');
        video.controls = false;
        video.muted = true;
        video.setAttribute('muted', 'true');
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        // prevent pointer events on video so custom overlay handles clicks
        video.style.pointerEvents = 'none';
      } catch (e) {}
    }

    function setPlayingState(isPlaying) {
      if (isPlaying) wrapper.classList.add('playing');
      else wrapper.classList.remove('playing');
    }

    function showFloating() {
      if (shown) return;
      shown = true;
      wrapper.classList.add('show');
      wrapper.setAttribute('aria-hidden', 'false');
      if (video) {
        try {
          video.muted = true; // ensure muted before play
          const p = video.play();
          if (p && typeof p.then === 'function') {
            p.then(() => {
              setPlayingState(!video.paused && !video.ended);
            }).catch(() => {
              setPlayingState(false);
            });
          } else {
            setPlayingState(!video.paused && !video.ended);
          }
        } catch (e) {
          setPlayingState(false);
        }
      }
    }

    function hideFloating(doPersist = true) {
      wrapper.classList.remove('show');
      wrapper.setAttribute('aria-hidden', 'true');
      try { if (video) video.pause(); } catch (e) {}
      setPlayingState(false);
      // persist close only when configured
      if (doPersist) markClosed(storageKey);
    }

    // Close button behavior: hide (and persist only if persistenceMode != 'none')
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        // For 'none' mode we still hide the card but don't persist
        hideFloating(persistenceMode !== 'none');
      });
    }

    // Overlay click: unmute & play; fallback to open if blocked
    if (overlay && video) {
      overlay.addEventListener('click', function (e) {
        e.preventDefault();
        try {
          video.muted = false;
          const p = video.play();
          if (p && typeof p.then === 'function') {
            p.then(() => setPlayingState(true)).catch(() => {
              const src = (video.querySelector('source') || {}).src;
              if (src) window.open(src, '_blank', 'noopener');
            });
          } else {
            setPlayingState(true);
          }
        } catch (err) {
          const src = (video.querySelector('source') || {}).src;
          if (src) window.open(src, '_blank', 'noopener');
        }
      });
    }

    if (video) {
      video.addEventListener('playing', function () { setPlayingState(true); });
      video.addEventListener('play', function () { setPlayingState(true); });
      video.addEventListener('pause', function () { setPlayingState(false); });
      video.addEventListener('ended', function () { setPlayingState(false); });
    }

    // show after scroll threshold
    function onScroll() {
      if (window.scrollY > 180) {
        showFloating();
        window.removeEventListener('scroll', onScroll);
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    if (window.scrollY > 180) showFloating();

    // SPA cleanup
    document.addEventListener('page:before-change', function () {
      try { if (video) video.pause(); } catch (e) {}
    });
  }

  function initAll() {
    document.querySelectorAll('.tt-floating-video').forEach(initFloating);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:reorder', initAll);
  document.addEventListener('shopify:section:select', initAll);
})();
